import {Component, DestroyRef, inject, OnInit} from "@angular/core";
import {BokkzIbDataService, IbRekenService, IncomeTaxEntity, OgBerekendeWaarde} from '@snelstart-ventures/fiscaal/domain-logic';
import {BehaviorSubject, filter, Observable, shareReplay, take} from "rxjs";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box3',
    templateUrl: './box3.component.html',
    styleUrls: ['./box3.component.scss'],
    imports: [SharedUiDetailsModule, ReactiveFormsModule, SharedUiFormControlsModule, CommonModule, SharedUiAlertModule],
})
export class Box3Component implements OnInit {
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(IbRekenService);
    public heeftOwr = false;
    public heeftPartner = false;
    public dataVisible$ = new BehaviorSubject<boolean>(false);

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        forfaitairRendement: new FormControl<number | null>(0),
        rendementsGrondslagNaVerdeling: new FormControl<number | null>(0),
        verminderingVoordeelSparenEnBeleggen: new FormControl<number | null>(0),
        voordeelSparenEnBeleggenVoorVermindering: new FormControl<number | null>(0),
        tijdsEvenredigeVermindering: new FormControl<number | null>(0),
        grondslagSparenEnBeleggen: new FormControl<number | null>(0),
        heffingsvrijVermogenAangever: new FormControl<number | null>(0),
        heffingsvrijVermogenPartner: new FormControl<number | null>(0),
        rendementsGrondslag: new FormControl<number | null>(0),
        banktegoedenNederland: new FormControl<number | null>(0),
        banktegoedenBuitenland: new FormControl<number | null>(0),
        beleggingen: new FormControl<number | null>(0),
        groeneBeleggingen: new FormControl<number | null>(0),
        vrijstellingGroeneBeleggingen: new FormControl<number | null>(0),
        groeneBeleggingenBanktegoeden: new FormControl<number | null>(0),
        groeneBeleggingenEffecten: new FormControl<number | null>(0),
        saldoGroeneBeleggingen: new FormControl<number | null>(0),
        onroerendeZaken: new FormControl<number | null>(0),
        onroerendeZakenNederland: new FormControl<number | null>(0),
        onroerendeZakenBuitenland: new FormControl<number | null>(0),
        waardePerOGNederland: new FormArray([
            new FormGroup({
                adres: new FormGroup({
                    straat: new FormControl<string | null>(null),
                    huisNummer: new FormControl<string | null>(null),
                    huisNummerToevoeging: new FormControl<number | null>(null),
                    postcode: new FormControl<string | null>(null),
                    woonplaats: new FormControl<string | null>(null),
                    landCode: new FormControl<string | null>(null),
                }),
                berekendeWaarde: new FormControl<number | null>(0),
                berekendeWaardeEindDatum: new FormControl<number | null>(0),
                percentageBelang: new FormControl<number | null>(0),
                aandeelBerekendeWaarde: new FormControl<number | null>(0),
                schuld: new FormControl<number | null>(0),
                waardeNaarTijdsgelang: new FormControl<number | null>(0),
                schuldNaarTijdsgelang: new FormControl<number | null>(0),
                waardeMinusSchuld: new FormControl<number | null>(0),
                waardeMinusSchuldNaarTijdsgelang: new FormControl<number | null>(0),
                datumVerkoop: new FormControl<Date | null>(null),
                datumAankoop: new FormControl<Date | null>(null),
                werkelijkRendement: new FormControl<number | null>(0),
            }),
        ]),
        waardePerOGBuitenland: new FormArray([
            new FormGroup({
                adres: new FormGroup({
                    straat: new FormControl<string | null>(null),
                    huisNummer: new FormControl<string | null>(null),
                    huisNummerToevoeging: new FormControl<number | null>(null),
                    postcode: new FormControl<string | null>(null),
                    woonplaats: new FormControl<string | null>(null),
                    landCode: new FormControl<string | null>(null),
                }),
                berekendeWaarde: new FormControl<number | null>(0),
                percentageBelang: new FormControl<number | null>(0),
                aandeelBerekendeWaarde: new FormControl<number | null>(0),
                schuld: new FormControl<number | null>(0),
                waardeNaarTijdsgelang: new FormControl<number | null>(0),
                schuldNaarTijdsgelang: new FormControl<number | null>(0),
                waardeMinusSchuld: new FormControl<number | null>(0),
                waardeMinusSchuldNaarTijdsgelang: new FormControl<number | null>(0),
                datumVerkoop: new FormControl<Date | null>(null),
                datumAankoop: new FormControl<Date | null>(null),
            }),
        ]),
        vorderingen: new FormControl<number | null>(0),
        kapitaalverzekeringen: new FormControl<number | null>(0),
        periodiekeUitkeringen: new FormControl<number | null>(0),
        crytoBezittingen: new FormControl<number | null>(0),
        overigeBezittingen: new FormControl<number | null>(0),
        premieDepots: new FormControl<number | null>(0),
        bouwDepots: new FormControl<number | null>(0),
        contantGeld: new FormControl<number | null>(0),
        nettoPensioen: new FormControl<number | null>(0),
        schuldenNederlandsOG: new FormControl<number | null>(0),
        schuldenBuitenlandsOG: new FormControl<number | null>(0),
        overigeSchulden: new FormControl<number | null>(0),
        totaleSchulden: new FormControl<number | null>(0),
        schuldenDrempel: new FormControl<number | null>(0),
        schuldenNaDrempel: new FormControl<number | null>(0),
        inkomenBox3: new FormControl<number | null>(0),
        rendementBanktegoedenNederland: new FormControl<number | null>(0),
        rendementBanktegoedenGroenNederland: new FormControl<number | null>(0),
        rendementBanktegoedenBuitenland: new FormControl<number | null>(0),
        rendementBeleggingen: new FormControl<number | null>(0),
        rendementBeleggingenGroen: new FormControl<number | null>(0),
        rendementOnroerendeZakenNederland: new FormControl<number | null>(0),
        rendementOnroerendeZakenBuitenland: new FormControl<number | null>(0),
        rendementVorderingen: new FormControl<number | null>(0),
        rendementContantGeld: new FormControl<number | null>(0),
        rendementCrypto: new FormControl<number | null>(0),
        rendementKapitaalverzekeringen: new FormControl<number | null>(0),
        rendementPeriodiekeUitkeringen: new FormControl<number | null>(0),
        rendementPremieDepots: new FormControl<number | null>(0),
        rendementBouwDepots: new FormControl<number | null>(0),
        rendementOverigeBezittingen: new FormControl<number | null>(0),
        rendementNettoPensioen: new FormControl<number | null>(0),
        rendementSchuldenNederlandsOG: new FormControl<number | null>(0),
        rendementSchuldenBuitenlandsOG: new FormControl<number | null>(0),
        rendementSchuldenOverig: new FormControl<number | null>(0),
        rendementAftrekbareSchulden: new FormControl<number | null>(0),
    });

    public formGroupWerkelijkRendement = new FormGroup({
        werkelijkRendement: new FormControl<number | null>(0),
        rendementBankrekeningenNederland: new FormControl<number | null>(0),
        rendementBankrekeningenBuitenland: new FormControl<number | null>(0),
        rendementBankrekeningenGroen: new FormControl<number | null>(0),
        rendementEffecten: new FormControl<number | null>(0),
        rendementEffectenGroen: new FormControl<number | null>(0),
        rendementOnroerendeZakenNl: new FormControl<number | null>(0),
        rendementOnroerendeZakenBtl: new FormControl<number | null>(0),
        rendementContantGeld: new FormControl<number | null>(0),
        rendementCrypto: new FormControl<number | null>(0),
        rendementBouwdepot: new FormControl<number | null>(0),
        rendementPremieDepot: new FormControl<number | null>(0),
        rendementVorderingen: new FormControl<number | null>(0),
        rendementKapitaalverzekeringen: new FormControl<number | null>(0),
        rendementPeriodiekeUitkeringen: new FormControl<number | null>(0),
        rendementOverigeBezittingen: new FormControl<number | null>(0),
        rendementSchuldenOgNederland: new FormControl<number | null>(0),
        rendementSchuldenOgBuitenland: new FormControl<number | null>(0),
        rendementSchuldenOgOverig: new FormControl<number | null>(0),
    });

    ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.heeftOwr = value.aangifte?.box3?.berekenWerkelijkRendement ?? false;
            this.heeftPartner = value.aangifte?.gezamenlijkeAangifte ?? false;
            this.bokkzClient.getIncomeTaxReport(value.aangifte!).subscribe((details) => {
                this.formGroup.reset({}, { emitEvent: false });
                this.formGroup.patchValue(details.specificatieBox3?.opbouw ?? {}, { emitEvent: false });
                this.formGroup.patchValue({ onroerendeZakenNederland: this.getOnroerendeZakenNederlandTotaal(details.specificatieBox3?.opbouw?.['waardePerOGNederland'],), }, { emitEvent: false },);
                this.formGroup.patchValue({ onroerendeZakenBuitenland: this.getOnroerendeZakenBuitenlandTotaal(details.specificatieBox3?.opbouw?.['waardePerOGBuitenland'],), }, { emitEvent: false },);
                this.formGroupWerkelijkRendement.reset({}, { emitEvent: false });
                this.formGroupWerkelijkRendement.patchValue(details.specificatieOwr?.opbouw ?? {}, { emitEvent: false });
                this.formGroup.disable({ emitEvent: false });
                this.formGroupWerkelijkRendement.disable({ emitEvent: false });
                this.dataVisible$.next(true);
            });
        });
    }

    berekenLaagsteVanRendementen(): any {
        if (this.heeftOwr) {
            const inkomenForfaitair = this.formGroup.get('inkomenBox3')?.value ?? 0;
            const inkomenWerkelijk = this.formGroupWerkelijkRendement.get('werkelijkRendement')?.value ?? 0;
            const inkomen = Math.min(inkomenForfaitair, inkomenWerkelijk);
            this.formGroup.patchValue({ forfaitairRendement: inkomen }, { emitEvent: false });
            return inkomen;
        } else {
            const inkomen = this.formGroup.get('inkomenBox3')?.value ?? 0;
            this.formGroup.patchValue({ forfaitairRendement: inkomen }, { emitEvent: false });
            return inkomen;
        }
    }

    private getOnroerendeZakenNederlandTotaal(waardePerOGNederland: OgBerekendeWaarde[] | undefined): number {
        return (
            waardePerOGNederland?.reduce((totaal, onroerendeZaak) => {
                return totaal + (onroerendeZaak.waardeNaarTijdsgelang ?? 0);
            }, 0) ?? 0
        );
    }

    private getOnroerendeZakenBuitenlandTotaal(waardePerOGBuitenland: OgBerekendeWaarde[] | undefined): number {
        return (
            waardePerOGBuitenland?.reduce((totaal, onroerendeZaak) => {
                return totaal + (onroerendeZaak.waardeNaarTijdsgelang ?? 0);
            }, 0) ?? 0
        );
    }
}
